import React, { useState, useEffect } from 'react';

// reactstrap components
import {
  Row,
  Col,
  FormGroup,
  Input,
  Button,
} from "reactstrap";

function SearchFilters({ filters, onFilterChange, onSearch, onClear, isLoading = false }) {
  const [criteria, setCriteria] = useState([]);

  // Available fields for search (requested set)
  const fieldOptions = [
    { value: '', label: 'Select Field' },
    { value: 'status', label: 'Status' },
    { value: 'createdBy', label: 'Created By' },
    { value: 'legalName', label: 'Legal Name' },
    { value: 'dbaName', label: 'DBA Name' },
    { value: 'createdAt', label: 'Date (Range)' },
    { value: 'federalTaxId', label: 'Tax ID' }
  ];

  // Available operators
  const operatorOptions = [
    { value: '=', label: 'equals' },
    { value: 'ILIKE', label: 'contains' }
  ];


  // Initialize with empty criteria
  useEffect(() => {
    if (criteria.length === 0) {
      addCriteria();
    }
  }, [criteria.length]);

  const addCriteria = () => {
    const newId = `criteria-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCriteria(prevCriteria => [...prevCriteria, { id: newId, field: '', operator: 'ILIKE', value: '' }]);
  };

  const removeCriteria = (id) => {
    setCriteria(prevCriteria => prevCriteria.filter(c => c.id !== id));
  };

  const updateCriteria = (id, field, value) => {
    setCriteria(prevCriteria => {
      const updated = prevCriteria.map(c => 
        c.id === id ? { ...c, [field]: value } : c
      );
      return updated;
    });
  };


  const handleSearch = () => {
    
    // Validate that we have at least one valid criterion
    const validCriteria = criteria.filter(c => {
      if (!c.field || c.field === '') return false;
      if (c.field === 'createdAt') {
        return Boolean((c.value && String(c.value).trim() !== '') || (c.value2 && String(c.value2).trim() !== ''));
      }
      return Boolean(c.value && String(c.value).trim() !== '');
    });
    
    if (validCriteria.length === 0) {
      onFilterChange({});
      onSearch();
      return;
    }
    
    // Convert criteria to filters format (same as merchant)
    const searchFilters = {};
    
    validCriteria.forEach(c => {
      
      if (c.field === 'createdAt') {
        // Date range special handling: keep visual operator BETWEEN but expose separate from/to
        if (c.value && String(c.value).trim() !== '') {
          searchFilters.createdAtFrom = c.value;
        }
        if (c.value2 && String(c.value2).trim() !== '') {
          searchFilters.createdAtTo = c.value2;
        }
        searchFilters.createdAt_operator = 'BETWEEN';
        return;
      }

      // Add the main field value
      searchFilters[c.field] = c.value.trim();
      // Add the operator for this field
      searchFilters[`${c.field}_operator`] = c.operator;
    });
    
    // Call the parent handlers
    onFilterChange(searchFilters);
    onSearch(searchFilters);
  };

  const handleClear = () => {
    const newId = `criteria-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    setCriteria([{ id: newId, field: '', operator: 'ILIKE', value: '', value2: '' }]);
    onClear();
  };

  const getOperatorOptions = (field) => {
    if (field === 'createdAt') {
      return [{ value: 'BETWEEN', label: 'between' }];
    }
    if (field === 'status') {
      return [
        { value: '=', label: 'equals' },
        { value: 'ILIKE', label: 'contains' }
      ];
    }
    return operatorOptions;
  };

  const hasValidCriteria = () => {
    const valid = criteria.some(c => c.field && c.field !== '' && c.value && c.value.trim() !== '');
    return valid;
  };

  const getValueInput = (criterion) => {
    const { field, value, value2 } = criterion;
    const todayStr = new Date().toISOString().slice(0,10);

    switch (field) {
      case 'createdAt':
        return (
          <div className="d-flex flex-column flex-sm-row align-items-stretch" style={{ minWidth: 0 }}>
            <div className="flex-fill mr-sm-2" style={{ minWidth: 0 }}>
              <small className="text-muted d-block mb-1">From:</small>
              <Input
                type="date"
                size="sm"
                value={value || ''}
                max={todayStr}
                onChange={(e) => updateCriteria(criterion.id, 'value', e.target.value)}
              />
            </div>
            <div className="flex-fill" style={{ minWidth: 0 }}>
              <small className="text-muted d-block mb-1">To:</small>
              <Input
                type="date"
                size="sm"
                value={value2 || ''}
                min={value || ''}
                max={todayStr}
                onChange={(e) => updateCriteria(criterion.id, 'value2', e.target.value)}
              />
            </div>
          </div>
        );
      case 'federalTaxId':
        return (
          <Input
            type="text"
            size="sm"
            placeholder="XX-XXXXXXX"
            maxLength="10"
            value={value || ''}
            onChange={(e) => {
              const digits = String(e.target.value || '').replace(/\D/g, '').slice(0, 9);
              const masked = digits.length <= 2 ? digits : `${digits.slice(0, 2)}-${digits.slice(2)}`;
              updateCriteria(criterion.id, 'value', masked);
            }}
          />
        );
      default:
        return (
          <Input
            type="text"
            size="sm"
            value={value || ''}
            onChange={(e) => {
              updateCriteria(criterion.id, 'value', e.target.value);
            }}
            placeholder={`Enter ${fieldOptions.find(f => f.value === field)?.label || 'value'}`}
          />
        );
    }
  };


  return (
    <div>
      {/* Search Criteria Section */}
      <div className="mb-2">
        <div className="d-flex justify-content-between align-items-center mb-1">
          <h6 className="mb-0 text-muted small">
            <i className="ni ni-zoom-split-in mr-1"></i>
            Search Criteria
          </h6>
        </div>

        {/* Criteria Rows */}
        {criteria.length === 0 ? (
          <div className="text-center py-2 border rounded bg-light">
            <i className="ni ni-zoom-split-in text-muted" style={{ fontSize: '1rem' }}></i>
            <p className="text-muted mt-1 mb-0 small">All applications shown by default. Add criteria to filter.</p>
          </div>
        ) : (
          <div className="border rounded bg-light" style={{ maxWidth: '900px' }}>
            {/* Table Headers */}
            <div className="p-2 border-bottom bg-white">
              <Row>
                <Col md="4">
                  <small className="text-muted font-weight-bold">Field</small>
                </Col>
                <Col md="3">
                  <small className="text-muted font-weight-bold">Operator</small>
                </Col>
                <Col md="5">
                  <small className="text-muted font-weight-bold">Value</small>
                </Col>
              </Row>
            </div>
            
            {/* Criteria Rows */}
            <div className="p-2">
              {criteria.map((criterion, index) => (
                <Row key={criterion.id} className={`align-items-end ${index > 0 ? 'mt-1' : ''}`}>
                  <Col md="4">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        size="sm"
                        value={criterion.field}
                        onChange={(e) => {
                          const selectedField = e.target.value;
                          updateCriteria(criterion.id, 'field', selectedField);
                          if (selectedField === 'createdAt') {
                            updateCriteria(criterion.id, 'operator', 'BETWEEN');
                          } else if (selectedField === 'status') {
                            updateCriteria(criterion.id, 'operator', '=');
                          } else {
                            updateCriteria(criterion.id, 'operator', 'ILIKE');
                          }
                          updateCriteria(criterion.id, 'value', '');
                          updateCriteria(criterion.id, 'value2', '');
                        }}
                      >
                        {fieldOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Input
                        type="select"
                        size="sm"
                        value={criterion.operator}
                        onChange={(e) => updateCriteria(criterion.id, 'operator', e.target.value)}
                      >
                        {getOperatorOptions(criterion.field).map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="5">
                    <FormGroup className="mb-0">
                      <div className="d-flex align-items-center" style={{ minWidth: 0 }}>
                        <div className="flex-grow-1" style={{ minWidth: 0 }}>
                          {getValueInput(criterion)}
                        </div>
                        <Button
                          color="link"
                          size="sm"
                          className="p-0 ml-1"
                          onClick={() => removeCriteria(criterion.id)}
                          title="Remove criteria"
                        >
                          <i className="ni ni-fat-remove text-danger"></i>
                        </Button>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add Criteria Button */}
      <div className="mb-2">
        <Button
          color="outline-primary"
          size="sm"
          onClick={addCriteria}
        >
          <i className="ni ni-fat-add mr-1"></i>
          Add Criteria
        </Button>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end">
        <Button
          color="warning"
          className="mr-2"
          onClick={handleClear}
          disabled={!hasValidCriteria()}
        >
          Clear
        </Button>
        <Button
          color="primary"
          onClick={handleSearch}
          disabled={criteria.length === 0 || isLoading || !hasValidCriteria()}
        >
          {isLoading ? 'Searching...' : `Search (${criteria.length} criteria)`}
        </Button>
      </div>
      <style>{`
        @media (min-width: 768px) and (max-width: 920px) {
          .form-control[type="date"] { max-width: 160px; }
        }
      `}</style>
    </div>
  );
}

export default SearchFilters;
